/** Iter 639 — vendor panel split · Step 4: done — KYC status pill/copy, permanent QR, stats + staff panels
 * (moved verbatim from pages/PartnerProgramPage.jsx). */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, ArrowRight, QrCode, Download, Copy, Check,
  AlertTriangle, Clock, UploadCloud,
} from "lucide-react";
import { sharedApi } from "@/lib/api";
import StaffTillPanel from "@/components/partner/StaffTillPanel";
import VendorStatsStrip from "@/components/partner/VendorStatsStrip";
import { ENGAGEMENT_HEADERS, ENGAGEMENT_PARAM, fetchVendorMe } from "@/features/vendorPanel/helpers";
import KycProgressPanel from "@/features/vendorPanel/KycProgressPanel";

export default function DoneCard({ v }) {
  const onReupload = () => v.setStep(2);
  const [qrObjectUrl, setQrObjectUrl] = useState(null);
  const [qrError, setQrError] = useState(false);

  // Iter-292 — poll GET /engagement/vendors/me every 15s so a live
  // approval propagates without a hard refresh.
  const meQ = useQuery({
    queryKey: ["partner-vendor-me"],
    refetchOnMount: "always",
    refetchInterval: 15_000,
    retry: false,
    queryFn: fetchVendorMe,
  });

  const kycStatus = meQ.data?.kyc_status || "pending";
  const rejectionReason = meQ.data?.kyc_rejection_reason || "";
  const qrUrl = meQ.data?.qr_url || "";
  const isApproved = kycStatus === "approved";
  // Iter-292 code-review fix: an outage previously appeared as "pending"
  // because a failed /vendors/me query left meQ.data undefined and the
  // fallback resolved to 'pending'. Surface a distinct fetch-error UI so
  // an approved vendor doesn't see fake "under review" copy.
  const statusFetchFailed = meQ.isError && !meQ.data;

  // Fetch the auth'd PNG only when approved — never speculatively (the
  // endpoint 403s until approved and would spam the console).
  useEffect(() => {
    if (!isApproved) {
      setQrObjectUrl(null);
      setQrError(false);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      try {
        const res = await sharedApi.get(
          "/classifieds/engagement/vendors/me/qr",
          {
            headers: ENGAGEMENT_HEADERS,
            params: ENGAGEMENT_PARAM,
            responseType: "blob",
          },
        );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setQrObjectUrl(objectUrl);
      } catch {
        if (!cancelled) setQrError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isApproved]);

  return (
    <div className="rounded-2xl bg-white shadow-card p-8 md:p-10 border border-emerald-100" data-testid="partner-done-card">
      <StatusPill status={kycStatus} loading={meQ.isLoading} error={statusFetchFailed} />

      <div className="mt-5 w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center">
        <CheckCircle2 className="w-7 h-7" />
      </div>
      <h2 className="mt-4 font-display text-2xl md:text-3xl font-black text-slate-900">
        You&apos;re in — welcome to the Partner Program.
      </h2>
      <StatusCopy status={kycStatus} reason={rejectionReason} onReupload={onReupload} />

      {/* Iter-316 addendum (a)+(b) — live KYC progress while not yet approved */}
      {!isApproved && meQ.data && (
        <KycProgressPanel vendor={meQ.data} />
      )}

      <div className="mt-6 grid md:grid-cols-[1fr_auto] gap-6 items-start">
        <div className="flex flex-wrap gap-3">
          <Link
            to="/post-ad"
            data-testid="partner-done-post-ad"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-6 py-3 transition-colors"
          >
            Post your first ad <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/account"
            data-testid="partner-done-account"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold px-6 py-3 transition-colors"
          >
            Go to Account
          </Link>
        </div>

        <VendorQr
          isApproved={isApproved}
          qrObjectUrl={qrObjectUrl}
          qrError={qrError}
          qrUrl={qrUrl}
        />
      </div>

      {/* Iter-325 — approved vendors manage restricted till staff here. */}
      {/* Iter-361 — stats strip sits ABOVE the staff/till section */}
      {isApproved && <VendorStatsStrip />}
      {isApproved && <StaffTillPanel vendorId={meQ.data?.id} />}
    </div>
  );
}

function StatusPill({ status, loading, error }) {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold uppercase tracking-widest px-3 py-1" data-testid="partner-kyc-pill-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Checking status…
      </span>
    );
  }
  if (error) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-[11px] font-bold uppercase tracking-widest px-3 py-1" data-testid="partner-kyc-pill-error">
        <AlertTriangle className="w-3.5 h-3.5" /> Status unavailable — please refresh
      </span>
    );
  }
  if (status === "approved") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-bold uppercase tracking-widest px-3 py-1" data-testid="partner-kyc-pill-approved">
        <CheckCircle2 className="w-3.5 h-3.5" /> Approved
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 text-[11px] font-bold uppercase tracking-widest px-3 py-1" data-testid="partner-kyc-pill-rejected">
        <AlertTriangle className="w-3.5 h-3.5" /> Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[11px] font-bold uppercase tracking-widest px-3 py-1" data-testid="partner-kyc-pill-pending">
      <Clock className="w-3.5 h-3.5" /> KYC under review
    </span>
  );
}

function StatusCopy({ status, reason, onReupload }) {
  if (status === "approved") {
    return (
      <p className="mt-2 text-slate-600 leading-relaxed">
        Your documents are approved. Print the QR below and place it at your till — customers scan it to unlock discounts.
      </p>
    );
  }
  if (status === "rejected") {
    return (
      <div className="mt-2" data-testid="partner-kyc-rejection">
        <p className="text-slate-700 leading-relaxed">
          Your documents were rejected. See the reason below and re-upload to try again.
        </p>
        {reason && (
          <blockquote
            data-testid="partner-kyc-rejection-reason"
            className="mt-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800 px-4 py-3 whitespace-pre-line"
          >
            {reason}
          </blockquote>
        )}
        <button
          type="button"
          onClick={onReupload}
          data-testid="partner-kyc-reupload"
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-red-500 hover:bg-red-600 text-white font-bold text-sm px-4 py-2 transition-colors"
        >
          <UploadCloud className="w-4 h-4" /> Re-upload documents
        </button>
      </div>
    );
  }
  return (
    <p className="mt-2 text-slate-600 leading-relaxed">
      We&apos;re reviewing your documents — you&apos;ll be able to accept customers once approved.
    </p>
  );
}

function VendorQr({ isApproved, qrObjectUrl, qrError, qrUrl }) {
  // Iter-302 — one-tap copy of the public scan URL so partners can drop
  // it into socials / receipts without downloading the PNG.
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(qrUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select the link below and copy it manually.");
    }
  };
  if (!isApproved) {
    return (
      <div
        data-testid="partner-qr-placeholder"
        className="w-44 h-44 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 grid place-items-center text-center p-3"
      >
        <div>
          <QrCode className="w-8 h-8 mx-auto text-slate-300" />
          <div className="mt-2 text-[11px] font-semibold text-slate-500 leading-tight">
            Your QR will appear here once KYC is approved
          </div>
        </div>
      </div>
    );
  }
  if (qrError) {
    return (
      <div data-testid="partner-qr-error" className="w-44 rounded-2xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
        Couldn&apos;t load your QR right now. Please refresh in a moment.
      </div>
    );
  }
  if (!qrObjectUrl) {
    return (
      <div data-testid="partner-qr-loading" className="w-44 h-44 rounded-2xl border border-soft bg-white grid place-items-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }
  return (
    <div className="w-44" data-testid="partner-qr-approved">
      <img
        src={qrObjectUrl}
        alt="Your till QR"
        className="w-44 h-44 rounded-2xl border border-soft bg-white p-2"
      />
      <a
        href={qrObjectUrl}
        download="allsale-till-qr.png"
        data-testid="partner-qr-download"
        className="mt-2 inline-flex items-center justify-center gap-2 w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold px-3 py-2 transition-colors"
      >
        <Download className="w-3.5 h-3.5" /> Download QR
      </a>
      {qrUrl && (
        <button
          type="button"
          onClick={copyLink}
          data-testid="partner-qr-copy-link"
          className={`mt-2 inline-flex items-center justify-center gap-2 w-full rounded-full text-xs font-bold px-3 py-2 transition-colors border ${
            copied
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
          }`}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Link copied" : "Copy QR link"}
        </button>
      )}
      {qrUrl && (
        <div className="mt-2 text-[10px] text-slate-400 text-center break-all">{qrUrl}</div>
      )}
    </div>
  );
}
